import { CompositionService } from "../../../librettoEditor/services/CompositionService";
import { deriveSequenceTitleFromFileNames, populateSequenceFromTracks } from "../sequenceAutoCreate";
import { insertSequenceIntoScriptLayer } from "../scriptInsertion/insertSequenceIntoScriptLayer";

type CreateSequenceFn = (args: {
    projectId: string;
    title: string;
}) => Promise<string | null | undefined>;
type GetTrackUrlFn = (args: { trackId: string }) => Promise<string | undefined>;
type CreateSpeakerFn = (args: {
    projectId: string;
    name: string;
}) => Promise<string | null | undefined>;

type Args = {
    orderedTracks: any[];
    explicitSequenceTitle?: string | null;
    projectId: string;
    selectedCompositionTitle?: string | null;
    userId: string;
    workspaceId?: string | null;
    createSequence: CreateSequenceFn;
    getTrackUrl: GetTrackUrlFn;
    createSpeaker?: CreateSpeakerFn;
};

export async function createMultiSpeakerSequenceAndScriptClip({
    orderedTracks,
    explicitSequenceTitle,
    projectId,
    selectedCompositionTitle,
    userId,
    workspaceId,
    createSequence,
    getTrackUrl,
    createSpeaker,
}: Args): Promise<void> {
    if (!orderedTracks.length) {
        throw new Error("No uploaded tracks found for multi-speaker editing.");
    }

    const projectIdForSequence = String(projectId || "").trim();
    if (!projectIdForSequence) {
        throw new Error("Missing project context for multi-speaker sequence creation.");
    }

    const resolvedUserId = String(userId || "").trim();
    if (!resolvedUserId) {
        throw new Error("Missing user identity for multi-speaker sequence persistence.");
    }

    const layerNames = orderedTracks.map((track: any, index: number) =>
        String(
            track?.title ||
                track?.filename ||
                track?.fileName ||
                track?.originalName ||
                track?.name ||
                `Track ${index + 1}`,
        ).trim() || `Track ${index + 1}`,
    );
    const sequenceTitle =
        String(explicitSequenceTitle || "").trim() ||
        deriveSequenceTitleFromFileNames(layerNames) ||
        (String(selectedCompositionTitle || "").trim() || "Multi-speaker recording");

    const newSequenceId = await createSequence({
        projectId: projectIdForSequence,
        title: sequenceTitle,
    });
    if (!newSequenceId) {
        throw new Error("Failed to create sequence for multi-speaker upload.");
    }

    const result = await populateSequenceFromTracks({
        sequenceId: newSequenceId,
        projectId: projectIdForSequence,
        userId: resolvedUserId,
        workspaceId,
        tracks: orderedTracks,
        layerNames,
    });
    if (!result.orderedLayerIds.length) {
        throw new Error("Could not persist sequence tracks for multi-speaker upload.");
    }

    if (!CompositionService.isInitialized()) {
        throw new Error("Composition is still loading. Please try again.");
    }

    const insertedScriptClip = await insertSequenceIntoScriptLayer({
        sequenceId: newSequenceId,
        userId: resolvedUserId,
        workspaceId,
        resolveTrackUrl: async (trackId) => getTrackUrl({ trackId }),
        createSpeaker,
    });
    if (!insertedScriptClip) {
        throw new Error("Failed to create script clip from multi-speaker upload.");
    }
}
